'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { WifiOff } from 'lucide-react';
import { APP_RESUME_EVENT } from '@/lib/native/events';
import { useIsNative } from '@/lib/native/use-is-native';

function subscribeToConnectivity(callback: () => void) {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

const getOnlineSnapshot = () => navigator.onLine;
const getServerSnapshot = () => true;

/**
 * Covers connectivity loss after the remote app has already loaded. The
 * native shell's offline.html only handles an unavailable initial URL.
 */
export function NativeOfflineBanner() {
  const native = useIsNative();
  const online = useSyncExternalStore(
    subscribeToConnectivity,
    getOnlineSnapshot,
    getServerSnapshot,
  );
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!native) {
      wasOffline.current = false;
      return;
    }
    if (!online) {
      wasOffline.current = true;
      return;
    }
    if (wasOffline.current) {
      wasOffline.current = false;
      // Reuse the foreground refresh path so cached feeds recover as soon as
      // the network returns, without discarding the current route or form.
      window.dispatchEvent(new CustomEvent(APP_RESUME_EVENT));
    }
  }, [native, online]);

  if (!native || online) return null;

  return (
    <div
      role="status"
      aria-live="assertive"
      className="fixed inset-x-3 z-[60] flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-50 px-3 py-2.5 text-amber-950 shadow-lg dark:bg-amber-950 dark:text-amber-50"
      style={{ bottom: 'calc(var(--app-bottom-inset) + 0.75rem)' }}
    >
      <WifiOff aria-hidden="true" className="h-4 w-4 shrink-0" />
      <span className="min-w-0 text-sm font-medium">
        You&apos;re offline. We&apos;ll reconnect automatically.
      </span>
    </div>
  );
}
